import { useCallback, useRef, useState } from 'react';

import { getApiHealth } from '../services/health';
import { apiVersionFrom } from '../config/buildInfo';

/**
 * The API's version for the account menu's footer, fetched LAZILY — `load()`
 * is called when the menu opens, not when the shell mounts, so a session that
 * never opens the menu never asks. One answer per page load: once /health has
 * answered (with a version, or without one from an older API) it is not asked
 * again. A failed request is not an answer, so the next open tries again.
 *
 * status: 'idle' | 'loading' | 'done' | 'failed'. `version` is null until
 * known, and stays null for an API that predates release identity.
 */
export default function useApiVersion() {
	const [state, setState] = useState({ status: 'idle', version: null });
	const busy = useRef(false);
	const settled = useRef(false);

	const load = useCallback(async () => {
		if (busy.current || settled.current) return;
		busy.current = true;
		setState(prev => ({ ...prev, status: 'loading' }));
		try {
			const body = await getApiHealth();
			settled.current = true;
			setState({ status: 'done', version: apiVersionFrom(body) });
		} catch {
			setState({ status: 'failed', version: null });
		} finally {
			busy.current = false;
		}
	}, []);

	return { ...state, load };
}
